import { readonly, ref, type Ref } from 'vue'

/**
 * Global connectivity tracker for surviving DPI/TSPU filtering windows.
 *
 * Context (2026-07-02 incident): RU carriers' DPI intermittently kills
 * browser-fingerprint TLS connections to our hostnames in windows lasting
 * MINUTES. The server stays healthy the whole time, so this is purely a
 * client-side transport problem: requests fail with no response at all.
 *
 * This module turns scattered per-request failures into one app-wide signal:
 *
 *   - axios interceptors call `reportNetworkFailure()` on no-response errors
 *     and `reportNetworkSuccess()` on any HTTP response;
 *   - ≥3 failures within 30s → degraded mode: `network:degraded` fires on
 *     window, `useNetworkHealth().degraded` flips, and a probe loop starts;
 *   - the probe treats ANY HTTP response — even a 404 — as proof the transport
 *     is alive (we detect a dead pipe, not backend health), so a plain
 *     `/public/health` URL works whether or not the route exists;
 *   - first success (probe or real request) → `network:recovered`, state resets.
 *
 * Consumers: the API clients extend their GET retry budget while degraded
 * (see networkRetry.ts) and NetworkStatusBanner tells the user what's going on.
 */

const FAILURE_THRESHOLD = 3
const FAILURE_WINDOW_MS = 30_000
const PROBE_INTERVAL_MS = 8_000
const PROBE_TIMEOUT_MS = 5_000

const degraded: Ref<boolean> = ref(false)

let probeUrl: string | null = null
let failureTimestamps: number[] = []
let probeTimer: ReturnType<typeof setTimeout> | null = null
let probeInFlight = false

/**
 * Point the tracker at a cheap same-API-origin URL. Call once per SPA (in the
 * api client module). The URL only needs to RESPOND — its status is irrelevant.
 */
export function configureNetworkHealth(url: string): void {
  probeUrl = url
}

export function isNetworkDegraded(): boolean {
  return degraded.value
}

/** Reactive view for UI (NetworkStatusBanner). */
export function useNetworkHealth(): { degraded: Readonly<Ref<boolean>> } {
  return { degraded: readonly(degraded) }
}

/** Report a no-response (connection-level) request failure. */
export function reportNetworkFailure(): void {
  const now = Date.now()
  failureTimestamps = failureTimestamps.filter((t) => now - t <= FAILURE_WINDOW_MS)
  failureTimestamps.push(now)
  if (!degraded.value && failureTimestamps.length >= FAILURE_THRESHOLD) {
    enterDegraded()
  }
}

/** Report that ANY HTTP response arrived — the transport is alive. */
export function reportNetworkSuccess(): void {
  failureTimestamps = []
  if (degraded.value) exitDegraded()
}

function enterDegraded(): void {
  degraded.value = true
  dispatch('network:degraded')
  scheduleProbe(PROBE_INTERVAL_MS)
}

function exitDegraded(): void {
  degraded.value = false
  failureTimestamps = []
  if (probeTimer) {
    clearTimeout(probeTimer)
    probeTimer = null
  }
  dispatch('network:recovered')
}

function dispatch(event: 'network:degraded' | 'network:recovered'): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(event))
  }
}

function scheduleProbe(delayMs: number): void {
  if (probeTimer) clearTimeout(probeTimer)
  // ±25% jitter: many tabs/users must not probe in lockstep.
  const jittered = delayMs * (0.75 + Math.random() * 0.5)
  probeTimer = setTimeout(() => {
    probeTimer = null
    void runProbe()
  }, jittered)
}

async function runProbe(): Promise<void> {
  if (!degraded.value || probeInFlight) return
  if (!probeUrl || typeof fetch === 'undefined') {
    // Not configured — degrade gracefully: recovery then comes only from a
    // real request succeeding via reportNetworkSuccess().
    return
  }
  probeInFlight = true
  const controller = new AbortController()
  // AbortSignal.timeout() needs Chrome 103+/Safari 16 — out of browserslist.
  const abortTimer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS)
  try {
    // Cache-bust so no intermediary can answer from cache; any HTTP status
    // (200/404/502…) resolves fetch → the pipe works.
    const sep = probeUrl.includes('?') ? '&' : '?'
    await fetch(`${probeUrl}${sep}_nhp=${Date.now()}`, {
      cache: 'no-store',
      signal: controller.signal,
    })
    reportNetworkSuccess()
  } catch {
    if (degraded.value) scheduleProbe(PROBE_INTERVAL_MS)
  } finally {
    clearTimeout(abortTimer)
    probeInFlight = false
  }
}

// A browser 'online' hint (Wi-Fi came back, VPN toggled) is a good moment to
// probe immediately instead of waiting out the current interval.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    if (degraded.value) scheduleProbe(0)
  })
}

/** Test-only: reset module state between cases. */
export function __resetNetworkHealthForTests(): void {
  degraded.value = false
  failureTimestamps = []
  probeInFlight = false
  if (probeTimer) {
    clearTimeout(probeTimer)
    probeTimer = null
  }
  probeUrl = null
}
